import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { Search, Users, Loader2, History, X, Mail, List, Columns3, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { STATUS_META, StatusPill, effectiveStatuses } from "@/lib/pipeline";
import { KanbanBoard, kanbanCards } from "@/components/KanbanBoard";
import { AreaStrip, PositionChips, StatusStrip } from "@/components/db/AreaStrip";
import { AreasDrawer } from "@/components/db/AreasDrawer";
import { ManageAreasModal } from "@/components/db/ManageAreasModal";
import { FilterPanel } from "@/components/db/FilterPanel";
import { CandidateTable, fmtDate } from "@/components/db/CandidateTable";
import { useNavigate } from "react-router-dom";

const COMM_LABELS = {
  created: "Kandidát pridaný do databázy",
  ack_default: "Automatický email (predvolený)",
  ack_custom: "Automatický email (vlastný)",
  privacy_notice: "GDPR informácia",
  contact: "Kontaktná správa",
};
const PAGE = 25;
const EMPTY_LOCAL = { q: "", skill: "", location: "", from: "" };

const candAreas = (c) => [...(c.areas || []), ...(c.positions || []).map((x) => (typeof x === "string" ? null : x.area)).filter(Boolean)];
const posNames = (c) => (c.positions || []).map((x) => (typeof x === "string" ? x : x.name));
const skillText = (c) => (c.skills || []).map((s) => (typeof s === "string" ? s : s.name)).join(" ").toLowerCase();

export default function Database() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [ov, setOv] = useState({ status_counts: {}, areas: [] });
  const [rejected, setRejected] = useState([]);
  const [local, setLocal] = useState(EMPTY_LOCAL);
  const [draft, setDraft] = useState({ q: "", position: "", status: "all" });
  const [selected, setSelected] = useState([]);
  const [assignArea, setAssignArea] = useState("");
  const [page, setPage] = useState(0);
  const [drawer, setDrawer] = useState(false);
  const [manage, setManage] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState(null);

  const area = sp.get("area") || "all";
  const position = sp.get("position") || "";
  const statuses = (sp.get("status") || "").split(",").filter((s) => STATUS_META[s]);
  const view = sp.get("view") || "list";
  const f = { area, position, statuses, ...local };

  const setF = useCallback((patch) => {
    const next = new URLSearchParams(sp);
    const put = (k, v) => (v && v !== "all" ? next.set(k, v) : next.delete(k));
    if ("area" in patch) { put("area", patch.area); if (!("position" in patch)) next.delete("position"); }
    if ("position" in patch) put("position", patch.position);
    if ("statuses" in patch) put("status", patch.statuses.join(","));
    if ("view" in patch) put("view", patch.view === "list" ? "" : patch.view);
    setSp(next);
    const loc = Object.fromEntries(Object.entries(patch).filter(([k]) => k in EMPTY_LOCAL));
    if (Object.keys(loc).length) setLocal((l) => ({ ...l, ...loc }));
    setPage(0);
  }, [sp, setSp]);

  useEffect(() => { setDraft({ q: local.q, position, status: statuses.length === 1 ? statuses[0] : "all" }); }, [local.q, position, sp.get("status")]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    const [d, o, r] = await Promise.all([
      api.get("/company/database", { params: { include_archived: true } }).catch(() => ({ data: { candidates: [] } })),
      api.get("/company/db/overview").catch(() => ({ data: { status_counts: {}, areas: [] } })),
      api.get("/company/db/rejected").catch(() => ({ data: { candidates: [] } })),
    ]);
    setData(d.data); setOv(o.data); setRejected(r.data.candidates || []);
    window.dispatchEvent(new Event("db-changed"));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openHistory = async (c) => {
    setHistFor(c); setHist(null);
    try { const r = await api.get(`/company/candidate/${c.id}/communications`); setHist(r.data.communications); }
    catch { setHist([]); }
  };
  const bulkAssign = async (action) => {
    if (!selected.length || !assignArea) { toast.error("Vyberte kandidátov a oblasť"); return; }
    try {
      const { data: r } = await api.post("/company/candidates/assign-area", { candidate_ids: selected, area: assignArea, action });
      toast.success(action === "remove" ? `Odobratých: ${r.modified}` : `Zaradených: ${r.modified}`); setSelected([]); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <CompanyShell wide><div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-600" /></div></CompanyShell>;

  const inArea = (c) => { const ca = candAreas(c); return area === "all" || ca.includes(area) || (area === "Nezaradení" && ca.length === 0); };
  const q = local.q.toLowerCase();
  const matches = (c) => inArea(c)
    && (!q || [c.name, c.title, ...posNames(c)].join(" ").toLowerCase().includes(q) || skillText(c).includes(q))
    && (!position || posNames(c).includes(position))
    && (!local.skill || skillText(c).includes(local.skill.toLowerCase()))
    && (!local.location || (c.location || "").toLowerCase().includes(local.location.toLowerCase()))
    && (!local.from || (c.created_at || "") >= local.from);
  const shreddedRows = rejected.map((r) => ({ ...r, shredded: true, positions: (r.positions || []).map((n) => ({ name: n })) }));
  const wantShred = statuses.includes("rejected_shredded");
  const pool = [...data.candidates, ...(wantShred ? shreddedRows : [])].filter(matches);
  const filtered = statuses.length ? pool.filter((c) => effectiveStatuses(c).some((s) => statuses.includes(s))) : pool.filter((c) => !c.shredded);
  const positionOptions = [...new Set([...data.candidates, ...shreddedRows].filter(inArea).flatMap(posNames))].sort();
  const areaRow = ov.areas.find((a) => a.area === area);
  const pageRows = filtered.slice(page * PAGE, page * PAGE + PAGE);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const applyBar = () => setF({ q: draft.q, position: draft.position, statuses: draft.status === "all" ? [] : [draft.status] });
  const kanbanPool = [...data.candidates, ...shreddedRows].filter(matches);
  const activeFilters = [area !== "all", !!position, statuses.length > 0, !!local.q, !!local.skill, !!local.location, !!local.from].filter(Boolean).length;
  // Z hlavného prehľadu sa oblasť/stav otvára v novom okne; v otvorenom okne sa filtruje na mieste
  const pickOrOpen = (patch) => {
    const isMain = area === "all" && statuses.length === 0;
    const target = patch.area ?? null;
    const sts = patch.statuses ?? null;
    if (isMain && ((target && target !== "all") || (sts && sts.length))) {
      const q = new URLSearchParams();
      if (target) q.set("area", target);
      if (sts?.length) q.set("status", sts.join(","));
      if (view === "kanban") q.set("view", "kanban");
      window.open(`/company/database?${q}`, "_blank", "noopener");
    } else setF(patch);
  };

  return (
    <CompanyShell wide>
      <div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-head text-3xl font-bold tracking-tight text-[#1e2a5a] flex items-center gap-3" data-testid="db-list-title">
                {area !== "all" ? area : "Všetci kandidáti"}
                <span className="rounded-full bg-violet-100 px-3 py-0.5 text-sm font-bold text-violet-700" data-testid="db-list-count">{filtered.length}</span>
              </h1>
              <p className="mt-1 text-slate-500">{area !== "all" ? `Kandidáti zaradení v oblasti ${area}` : "Prehľad všetkých kandidátov v databáze"}</p>
            </div>
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-testid="view-toggle">
              {[["list", "Zoznam", List], ["kanban", "Kanban", Columns3]].map(([v, l, I]) => (
                <button key={v} data-testid={`view-${v}`} onClick={() => setF({ view: v })}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${view === v ? "bg-violet-600 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}><I size={15} /> {l}</button>
              ))}
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); applyBar(); }} className="mt-5 flex flex-wrap gap-3" data-testid="filter-bar">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="database-search" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} placeholder="Hľadať meno, pozíciu, zručnosť…"
                className="w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 py-2.5 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
            </div>
            <select data-testid="position-filter" value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-violet-500 min-w-[170px]">
              <option value="">Všetky pozície</option>
              {positionOptions.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <select data-testid="status-filter" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-violet-500 min-w-[160px]">
              <option value="all">Všetky stavy</option>
              {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.l}</option>)}
            </select>
            <button type="button" data-testid="filter-apply" onClick={() => { applyBar(); setFilterOpen((o) => !o); }}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${filterOpen ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-700 hover:border-violet-300 hover:text-violet-700"}`}>
              <Filter size={15} /> Filtrovať{activeFilters > 0 && <span className="rounded-full bg-violet-600 px-1.5 text-[10px] text-white" data-testid="active-filter-count">{activeFilters}</span>}
            </button>
          </form>
          {filterOpen && (
            <FilterPanel f={f} setF={setF} areas={ov.areas} statusCounts={ov.status_counts} positionOptions={positionOptions}
              onClose={() => setFilterOpen(false)} onClear={() => { setLocal(EMPTY_LOCAL); setSp(view === "kanban" ? { view } : {}); }} />
          )}

          <AreaStrip areas={ov.areas} total={ov.status_counts.all || 0} active={area} onPick={(a) => pickOrOpen({ area: a })}
            onShowAll={() => setDrawer(true)} onManage={() => setManage(true)} />
          {area !== "all" && areaRow && <PositionChips area={area} positions={areaRow.positions} active={position} onPick={(p) => setF({ position: p })} />}
          {area === "all"
            ? <StatusStrip counts={ov.status_counts} active={statuses} onPick={(s) => pickOrOpen({ statuses: s })} />
            : <StatusStrip counts={areaRow?.status_counts || {}} active={statuses} onPick={(s) => setF({ statuses: s })}
                title={`Prehľad kandidátov v oblasti ${area} podľa stavu`} testId="area-status-strip" />}

          {selected.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl bg-violet-50 border border-violet-100 p-3" data-testid="bulk-bar">
              <span className="text-sm font-semibold text-violet-800">{selected.length} vybraných</span>
              <select data-testid="bulk-area-select" value={assignArea} onChange={(e) => setAssignArea(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Vyberte oblasť…</option>
                {ov.areas.map((a) => <option key={a.area} value={a.area}>{a.area}</option>)}
              </select>
              <button data-testid="bulk-assign-btn" onClick={() => bulkAssign("add")} disabled={!assignArea} className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Zaradiť</button>
              <button data-testid="bulk-remove-btn" onClick={() => bulkAssign("remove")} disabled={!assignArea} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Odobrať z oblasti</button>
              <button data-testid="bulk-clear-btn" onClick={() => setSelected([])} className="ml-auto text-sm text-slate-500 hover:text-slate-800">Zrušiť výber</button>
            </div>
          )}

          <div className="mt-6">
            {view === "kanban" ? (
              <KanbanBoard reload={reload} cards={kanbanCards(kanbanPool).filter((x) => (!statuses.length || statuses.includes(x.status))
                && (area === "all" || !x.posId || x.area === area) && (!position || !x.posId || x.position === position))} />
            ) : filtered.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="db-empty">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><Users className="text-slate-400" /></div>
                <p className="mt-4 font-semibold text-slate-800">Žiadni kandidáti</p>
                <p className="text-sm text-slate-500 mt-1">Skúste upraviť filtre alebo nahrajte nové CV.</p>
              </div>
            ) : statuses.length === 1 && wantShred ? (
              <RejectedRows rows={pageRows} />
            ) : (
              <CandidateTable rows={pageRows} reload={reload} openHistory={openHistory} selected={selected}
                onToggle={(id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
                onToggleAll={() => setSelected((s) => (pageRows.every((c) => s.includes(c.id)) ? s.filter((id) => !pageRows.some((c) => c.id === id)) : [...new Set([...s, ...pageRows.map((c) => c.id)])]))} />
            )}
            {view === "list" && filtered.length > 0 && (
              <div className="mt-4 flex items-center justify-between text-sm text-slate-500" data-testid="pagination">
                <span>Zobrazených {pageRows.length} z {filtered.length} kandidátov</span>
                <div className="flex items-center gap-1">
                  <button data-testid="page-prev" disabled={page === 0} onClick={() => setPage(page - 1)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
                  <span className="rounded-lg bg-violet-600 px-3 py-1 text-xs font-bold text-white" data-testid="page-current">{page + 1}</span>
                  <span className="text-xs">/ {pages}</span>
                  <button data-testid="page-next" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronRight size={16} /></button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawer && <AreasDrawer areas={ov.areas} onClose={() => setDrawer(false)} onPick={(a) => { setDrawer(false); pickOrOpen({ area: a }); }} />}
      {manage && <ManageAreasModal areas={ov.areas} onClose={() => setManage(false)} reload={reload} />}
      {histFor && <CommHistory c={histFor} hist={hist} onClose={() => setHistFor(null)} />}
    </CompanyShell>
  );
}

function CommHistory({ c, hist, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-3xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="comm-history-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-head text-xl font-bold flex items-center gap-2 text-[#1e2a5a]"><History size={20} className="text-violet-600" /> História komunikácie</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="mt-1 text-sm text-slate-500">{c.name}</p>
        {hist === null ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-violet-600" /></div>
          : hist.length === 0 ? (
            <div className="mt-6 text-center py-8 text-sm text-slate-500" data-testid="comm-history-empty"><Mail className="mx-auto text-slate-300 mb-2" size={28} /> Zatiaľ neboli odoslané žiadne emaily.</div>
          ) : (
            <ol className="mt-5 relative border-l-2 border-slate-100 ml-2 space-y-5" data-testid="comm-history-list">
              {hist.map((e) => (
                <li key={e.id} className="ml-5">
                  <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full bg-violet-600 border-2 border-white" />
                  <div className="text-sm font-semibold text-slate-800">{COMM_LABELS[e.type] || e.type}</div>
                  <div className="text-sm text-slate-600">{e.subject}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{new Date(e.created_at).toLocaleString("sk-SK")}{e.to_email ? ` · ${e.to_email}` : ""}{e.by_name ? ` · ${e.by_name}` : ""}</div>
                </li>
              ))}
            </ol>
          )}
      </div>
    </div>
  );
}

function RejectedRows({ rows }) {
  const nav = useNavigate();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="rejected-list">
      {rows.map((c) => (
        <button key={c.id} data-testid={`rejected-row-${c.id}`} onClick={() => nav(`/company/candidate/${c.id}`)}
          className="w-full flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100 last:border-0 text-left hover:bg-slate-50 transition-colors">
          <span className="w-48 font-semibold text-[#1e2a5a] truncate">{c.name}</span>
          <span className="flex-1 min-w-[8rem] text-sm text-slate-500 truncate">{posNames(c).join(", ") || (c.areas || []).join(", ") || "—"}</span>
          <span className="text-xs text-slate-500">{fmtDate(c.created_at)}</span>
          <span className="w-12 text-center text-sm font-bold text-violet-700">{typeof c.career_score === "number" ? c.career_score : "—"}</span>
          <StatusPill status="rejected_shredded" />
        </button>
      ))}
    </div>
  );
}
